import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "../App";
import { useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import {
  BookOpen, Plus, Trash2, Edit, Play, ChevronLeft, Upload,
  GraduationCap, TrendingUp, Award, Clock, CheckCircle, Video, BarChart3,
  X, Save, Eye, Download, Users
} from "lucide-react";
import { Button } from "../components/ui/button";
import VisibilitySelector from "../components/VisibilitySelector";

const TABS = { CURSOS: "cursos", MI_PROGRESO: "mi_progreso", DASHBOARD: "dashboard", CERTIFICADOS: "certificados" };

export default function CapacitacionesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "Administrador";
  const isGH = (user?.department || "").toLowerCase().includes("gesti") || (user?.department || "").toLowerCase().includes("rrhh");
  const canSeeAll = isAdmin || isGH;

  const getInitialTab = () => {
    if (location.pathname.includes("mi-progreso")) return TABS.MI_PROGRESO;
    if (location.pathname.includes("dashboard")) return TABS.DASHBOARD;
    if (location.pathname.includes("certificados")) return TABS.CERTIFICADOS;
    return TABS.CURSOS;
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  const tabs = [
    { id: TABS.CURSOS, label: "Cursos", icon: BookOpen },
    { id: TABS.MI_PROGRESO, label: "Mi Progreso", icon: TrendingUp },
    ...(canSeeAll || user?.role === "supervisor" || user?.role === "Supervisor" ? [
      { id: TABS.DASHBOARD, label: "Dashboard", icon: BarChart3 }
    ] : []),
    ...(canSeeAll ? [{ id: TABS.CERTIFICADOS, label: "Certificados", icon: Award }] : [])
  ];

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 overflow-auto" data-testid="capacitaciones-page">
        <div className="flex items-center gap-3 mb-6">
          <GraduationCap className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Capacitaciones y Cursos</h1>
        </div>
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto mb-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>
        {activeTab === TABS.CURSOS && <CursosTab />}
        {activeTab === TABS.MI_PROGRESO && <MiProgresoTab />}
        {activeTab === TABS.DASHBOARD && <DashboardTab />}
        {activeTab === TABS.CERTIFICADOS && <CertificadosTab />}
      </main>
    </div>
  );
}

function CursosTab() {
  const { user, getAuthHeaders } = useAuth();
  const [cursos, setCursos] = useState([]);
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCurso, setEditingCurso] = useState(null);
  const isAdmin = user?.role === "admin" || user?.role === "Administrador";

  const fetchCursos = useCallback(async () => {
    try { const res = await axios.get(`${API}/capacitaciones/cursos`, getAuthHeaders()); setCursos(res.data); } catch (e) { console.error(e); }
  }, [getAuthHeaders]);
  useEffect(() => { fetchCursos(); }, [fetchCursos]);

  if (selectedCurso) return <CursoDetalle cursoId={selectedCurso} onBack={() => { setSelectedCurso(null); fetchCursos(); }} />;

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Button data-testid="btn-nuevo-curso" onClick={() => { setEditingCurso(null); setShowForm(true); }}><Plus className="w-4 h-4 mr-2" />Nuevo Curso</Button>
        </div>
      )}
      {showForm && <CursoForm curso={editingCurso} onSave={() => { setShowForm(false); fetchCursos(); }} onCancel={() => setShowForm(false)} />}
      {cursos.length === 0 ? (
        <div className="text-center py-16 text-gray-500"><GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-40" /><p>No hay cursos disponibles</p>{isAdmin && <p className="text-sm mt-2">Cree el primer curso con el botón de arriba</p>}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {cursos.map(curso => (
            <div key={curso.id} data-testid={`curso-card-${curso.id}`}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedCurso(curso.id)}>
              <div className="flex items-start justify-between mb-3">
                <BookOpen className="w-8 h-8 text-blue-500" />
                {isAdmin && (
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <button className="p-1.5 text-gray-400 hover:text-blue-500" onClick={() => { setEditingCurso(curso); setShowForm(true); }}><Edit className="w-4 h-4" /></button>
                    <button className="p-1.5 text-gray-400 hover:text-red-500" onClick={async () => { if (window.confirm("¿Eliminar este curso?")) { await axios.delete(`${API}/capacitaciones/cursos/${curso.id}`, getAuthHeaders()); fetchCursos(); } }}><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{curso.titulo}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">{curso.descripcion}</p>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" />{curso.total_videos || 0} videos</span>
                {curso.categoria && <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{curso.categoria}</span>}
                {curso.tiene_certificado && <Award className="w-3.5 h-3.5 text-amber-500" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CursoForm({ curso, onSave, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({
    titulo: curso?.titulo || "", descripcion: curso?.descripcion || "", categoria: curso?.categoria || "",
    departamentos: curso?.departamentos || [], tiene_certificado: curso?.tiene_certificado || false, orden: curso?.orden || 0,
    visibilidad: curso?.visibilidad || "todos",
    departamentos_permitidos: curso?.departamentos_permitidos || [],
    usuarios_permitidos: curso?.usuarios_permitidos || []
  });
  const [certFile, setCertFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleVisChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let cursoId = curso?.id;
      if (curso?.id) {
        await axios.put(`${API}/capacitaciones/cursos/${curso.id}`, form, getAuthHeaders());
      } else {
        const res = await axios.post(`${API}/capacitaciones/cursos`, form, getAuthHeaders());
        cursoId = res.data.id;
      }
      if (certFile && cursoId) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", certFile);
        await axios.post(`${API}/capacitaciones/cursos/${cursoId}/certificado-template`, fd, {
          ...getAuthHeaders(),
          headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
        });
      }
      onSave();
    } catch (err) { console.error(err); }
    setUploading(false);
  };
  return (
    <form onSubmit={handleSubmit} data-testid="curso-form" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
      <h3 className="font-semibold text-lg">{curso ? "Editar Curso" : "Nuevo Curso"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="block text-sm font-medium mb-1">Título *</label><input data-testid="input-titulo-curso" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required /></div>
        <div><label className="block text-sm font-medium mb-1">Categoría</label><input className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.categoria} onChange={e => setForm({...form, categoria: e.target.value})} /></div>
      </div>
      <div><label className="block text-sm font-medium mb-1">Descripción</label><textarea className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" rows={3} value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} /></div>

      {/* Visibility Selector */}
      <VisibilitySelector
        visibilidad={form.visibilidad}
        departamentos_permitidos={form.departamentos_permitidos}
        usuarios_permitidos={form.usuarios_permitidos}
        onChange={handleVisChange}
        helperEndpoint="/capacitaciones"
      />

      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.tiene_certificado} onChange={e => setForm({...form, tiene_certificado: e.target.checked})} />Este curso otorga certificado al completarse</label>
      {form.tiene_certificado && (
        <div className="border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/10">
          <label className="block text-sm font-medium mb-2"><Upload className="w-4 h-4 inline mr-1" />Subir PDF de Certificado (opcional)</label>
          <p className="text-xs text-gray-500 mb-2">Suba un PDF personalizado que se entregará al completar el curso. Si no sube uno, se generará un certificado automático.</p>
          <input type="file" accept=".pdf" data-testid="input-cert-pdf" onChange={e => setCertFile(e.target.files?.[0] || null)} className="text-sm" />
          {curso?.certificado_template_url && <p className="text-xs text-green-600 mt-2"><CheckCircle className="w-3 h-3 inline mr-1" />Ya tiene un PDF de certificado cargado</p>}
        </div>
      )}
      <div className="flex gap-2 justify-end"><Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button><Button type="submit" data-testid="btn-guardar-curso" disabled={uploading}><Save className="w-4 h-4 mr-2" />{uploading ? "Subiendo..." : "Guardar"}</Button></div>
    </form>
  );
}

function CursoDetalle({ cursoId, onBack }) {
  const { user, getAuthHeaders } = useAuth();
  const [curso, setCurso] = useState(null);
  const [progreso, setProgreso] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "Administrador";

  const fetchCurso = useCallback(async () => {
    try {
      const [cursoRes, progresoRes] = await Promise.all([
        axios.get(`${API}/capacitaciones/cursos/${cursoId}`, getAuthHeaders()),
        axios.get(`${API}/capacitaciones/progreso/${cursoId}`, getAuthHeaders())
      ]);
      setCurso(cursoRes.data);
      setProgreso(progresoRes.data);
    } catch (e) { console.error(e); }
  }, [cursoId, getAuthHeaders]);
  useEffect(() => { fetchCurso(); }, [fetchCurso]);

  const getVideoProgreso = (videoId) => progreso.find(p => p.video_id === videoId);
  const handleDescargarCertificado = async () => {
    try {
      const res = await axios.get(`${API}/capacitaciones/certificado/${cursoId}/generar`, { ...getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", `certificado_${cursoId}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { alert(err.response?.data?.detail || "Error al generar certificado"); }
  };

  if (!curso) return <div className="text-center py-8">Cargando...</div>;
  const videos = curso.videos || [];
  const totalCompleted = videos.filter(v => getVideoProgreso(v.id)?.completado).length;
  const cursoCompletado = videos.length > 0 && totalCompleted === videos.length;

  return (
    <div className="space-y-6" data-testid="curso-detalle">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"><ChevronLeft className="w-4 h-4" />Volver a cursos</button>
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div><h2 className="text-xl font-bold text-gray-900 dark:text-white">{curso.titulo}</h2><p className="text-gray-500 mt-1">{curso.descripcion}</p></div>
          {curso.tiene_certificado && cursoCompletado && (
            <Button data-testid="btn-descargar-cert" onClick={handleDescargarCertificado} className="bg-amber-500 hover:bg-amber-600"><Download className="w-4 h-4 mr-2" />Certificado</Button>
          )}
        </div>
        {videos.length > 0 && (
          <div className="mb-2">
            <div className="flex items-center justify-between text-sm mb-1"><span>Progreso: {totalCompleted}/{videos.length} videos</span><span>{Math.round(totalCompleted / videos.length * 100)}%</span></div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5"><div className="bg-blue-600 h-2.5 rounded-full transition-all" style={{ width: `${totalCompleted / videos.length * 100}%` }} /></div>
          </div>
        )}
      </div>
      {activeVideo && <VideoPlayer video={activeVideo} progreso={getVideoProgreso(activeVideo.id)} onClose={() => { setActiveVideo(null); fetchCurso(); }} />}
      {isAdmin && <div className="flex gap-2"><Button data-testid="btn-agregar-video" size="sm" onClick={() => setShowVideoForm(true)}><Plus className="w-4 h-4 mr-2" />Agregar Video</Button></div>}
      {showVideoForm && <VideoForm cursoId={cursoId} onSave={() => { setShowVideoForm(false); fetchCurso(); }} onCancel={() => setShowVideoForm(false)} />}
      <div className="space-y-2">
        {videos.map((video, idx) => {
          const vp = getVideoProgreso(video.id);
          return (
            <div key={video.id} data-testid={`video-item-${video.id}`}
              className="flex items-center gap-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow transition-shadow cursor-pointer"
              onClick={() => setActiveVideo(video)}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${vp?.completado ? "bg-green-100 text-green-600" : "bg-blue-100 text-blue-600"}`}>
                {vp?.completado ? <CheckCircle className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 dark:text-white">{idx + 1}. {video.titulo}</h4>
                <p className="text-xs text-gray-500">{video.descripcion}</p>
                {vp && !vp.completado && <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5"><div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${vp.porcentaje || 0}%` }} /></div>}
              </div>
              <div className="text-xs text-gray-400">{vp?.completado ? "Completado" : vp ? `${vp.porcentaje || 0}%` : "Sin iniciar"}</div>
              {isAdmin && <button className="p-1 text-gray-400 hover:text-red-500" onClick={async (e) => { e.stopPropagation(); if (window.confirm("¿Eliminar video?")) { await axios.delete(`${API}/capacitaciones/videos/${video.id}`, getAuthHeaders()); fetchCurso(); } }}><Trash2 className="w-4 h-4" /></button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VideoForm({ cursoId, onSave, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({ titulo: "", descripcion: "", tipo: "youtube", url: "", duracion_segundos: 0, orden: 0 });
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (form.tipo === "upload") {
        if (!videoFile) return alert("Seleccione un archivo de video");
        if (videoFile.size > 500 * 1024 * 1024) return alert("El archivo excede el límite de 500MB");
        setUploading(true);
        setUploadProgress(0);
        const fd = new FormData();
        fd.append("file", videoFile);
        fd.append("titulo", form.titulo || videoFile.name);
        fd.append("descripcion", form.descripcion);
        fd.append("orden", form.orden.toString());
        await axios.post(`${API}/capacitaciones/cursos/${cursoId}/videos/upload`, fd, {
          ...getAuthHeaders(),
          headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round(e.loaded / e.total * 100)); }
        });
      } else {
        if (!form.url) return alert("Ingrese la URL del video");
        await axios.post(`${API}/capacitaciones/cursos/${cursoId}/videos`, form, getAuthHeaders());
      }
      onSave();
    } catch (err) { console.error(err); alert("Error al guardar el video"); }
    setUploading(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="video-form" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
      <h4 className="font-semibold">Agregar Video</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="block text-sm font-medium mb-1">Título *</label><input data-testid="input-titulo-video" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required={form.tipo !== "upload"} placeholder={form.tipo === "upload" ? "Se usará el nombre del archivo si está vacío" : ""} /></div>
        <div>
          <label className="block text-sm font-medium mb-1">Tipo</label>
          <select className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
            <option value="youtube">YouTube</option>
            <option value="vimeo">Vimeo</option>
            <option value="url">URL directa</option>
            <option value="upload">Subir archivo de video</option>
          </select>
        </div>
      </div>
      {form.tipo === "upload" ? (
        <div className="border-2 border-dashed border-indigo-300 dark:border-indigo-700 rounded-lg p-4 bg-indigo-50 dark:bg-indigo-900/10">
          <label className="block text-sm font-medium mb-2"><Upload className="w-4 h-4 inline mr-1" />Seleccionar archivo de video</label>
          <p className="text-xs text-gray-500 mb-2">Formatos: MP4, WebM, MOV, AVI. Máximo 500MB.</p>
          <input type="file" accept="video/*,.mp4,.webm,.mov,.avi,.mkv" data-testid="input-video-file"
            onChange={e => setVideoFile(e.target.files?.[0] || null)} className="text-sm" />
          {videoFile && (
            <p className="text-xs text-indigo-600 mt-2">Seleccionado: {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(1)} MB)</p>
          )}
          {uploading && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2"><div className="bg-indigo-600 h-2 rounded-full transition-all" style={{width: `${uploadProgress}%`}} /></div>
              <p className="text-xs text-indigo-600 mt-1">{uploadProgress}% subido...</p>
            </div>
          )}
        </div>
      ) : (
        <div><label className="block text-sm font-medium mb-1">URL del Video *</label><input data-testid="input-url-video" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder={form.tipo === "youtube" ? "https://youtube.com/watch?v=..." : form.tipo === "vimeo" ? "https://vimeo.com/..." : "https://..."} required /></div>
      )}
      <div><label className="block text-sm font-medium mb-1">Descripción</label><textarea className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" rows={2} value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} /></div>
      <div className="flex gap-2 justify-end"><Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button><Button type="submit" data-testid="btn-guardar-video" disabled={uploading}><Save className="w-4 h-4 mr-2" />{uploading ? `Subiendo ${uploadProgress}%...` : "Guardar"}</Button></div>
    </form>
  );
}

function VideoPlayer({ video, progreso, onClose }) {
  const { getAuthHeaders } = useAuth();
  const videoRef = useRef(null);
  const intervalRef = useRef(null);
  const maxWatchedRef = useRef(progreso?.posicion_segundos || 0);
  const [watchPercent, setWatchPercent] = useState(progreso?.porcentaje || 0);
  const [isCompleted, setIsCompleted] = useState(progreso?.completado || false);

  const getEmbedUrl = (v) => {
    if (v.tipo === "youtube") { const m = v.url.match(/(?:v=|\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]+)/); if (m) return `https://www.youtube.com/embed/${m[1]}?enablejsapi=1`; }
    if (v.tipo === "vimeo") { const m = v.url.match(/vimeo\.com\/(\d+)/); if (m) return `https://player.vimeo.com/video/${m[1]}`; }
    return v.url;
  };

  const saveProgress = useCallback(async (pos, dur, completed = false) => {
    try {
      const res = await axios.post(`${API}/capacitaciones/progreso/${video.id}`, { posicion_segundos: pos, duracion_total: dur, completado: completed }, getAuthHeaders());
      if (res.data.porcentaje) setWatchPercent(res.data.porcentaje);
      if (res.data.completado) setIsCompleted(true);
    } catch (e) { console.error(e); }
  }, [video.id, getAuthHeaders]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || video.tipo !== "upload") return;

    // Resume from last position
    if (progreso?.posicion_segundos) {
      el.currentTime = progreso.posicion_segundos;
      maxWatchedRef.current = progreso.posicion_segundos;
    }

    // Prevent seeking ahead
    const handleSeeking = () => {
      if (el.currentTime > maxWatchedRef.current + 2) {
        el.currentTime = maxWatchedRef.current;
      }
    };

    // Track max watched position
    const handleTimeUpdate = () => {
      if (el.currentTime > maxWatchedRef.current) {
        maxWatchedRef.current = el.currentTime;
      }
      if (el.duration > 0) {
        setWatchPercent(Math.min(100, Math.round((maxWatchedRef.current / el.duration) * 100)));
      }
    };

    el.addEventListener("seeking", handleSeeking);
    el.addEventListener("timeupdate", handleTimeUpdate);
    intervalRef.current = setInterval(() => { if (el && !el.paused) saveProgress(maxWatchedRef.current, el.duration); }, 10000);
    el.addEventListener("ended", () => saveProgress(el.duration, el.duration, true));

    return () => {
      el.removeEventListener("seeking", handleSeeking);
      el.removeEventListener("timeupdate", handleTimeUpdate);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [video, progreso, saveProgress]);

  const canMarkComplete = watchPercent >= 98;

  // Track time for external videos (YouTube/Vimeo)
  const [externalSeconds, setExternalSeconds] = useState(0);
  const externalTimerRef = useRef(null);
  useEffect(() => {
    if (video.tipo !== "youtube" && video.tipo !== "vimeo") return;
    externalTimerRef.current = setInterval(() => {
      setExternalSeconds(prev => {
        const next = prev + 1;
        // Auto-save every 30s
        if (next % 30 === 0) saveProgress(next, video.duracion_segundos || 600, false);
        return next;
      });
    }, 1000);
    return () => { if (externalTimerRef.current) clearInterval(externalTimerRef.current); };
  }, [video, saveProgress]);

  useEffect(() => {
    if (video.tipo === "youtube" || video.tipo === "vimeo") {
      const dur = video.duracion_segundos || 600;
      if (dur > 0) setWatchPercent(Math.min(100, Math.round((externalSeconds / dur) * 100)));
    }
  }, [externalSeconds, video]);

  return (
    <div className="bg-black rounded-lg overflow-hidden relative" data-testid="video-player">
      <div className="flex justify-between items-center bg-gray-900 px-4 py-2">
        <h4 className="text-white font-medium text-sm">{video.titulo}</h4>
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-xs">{watchPercent}% visto</span>
          <button onClick={() => { if (video.tipo === "upload") saveProgress(maxWatchedRef.current, videoRef.current?.duration || 0); onClose(); }} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="aspect-video bg-black">
        {video.tipo === "upload" ? (
          <video ref={videoRef} controls controlsList="nodownload" className="w-full h-full" src={video.url} style={{ pointerEvents: "auto" }} />
        ) : (
          <iframe src={getEmbedUrl(video)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={video.titulo} />
        )}
      </div>
      {/* Progress bar */}
      <div className="bg-gray-800 px-4 py-1">
        <div className="w-full bg-gray-700 rounded-full h-1.5"><div className={`h-1.5 rounded-full transition-all ${canMarkComplete ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${watchPercent}%` }} /></div>
      </div>
      {(video.tipo === "youtube" || video.tipo === "vimeo") && (
        <div className="bg-gray-900 px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs">{isCompleted ? "Video completado" : canMarkComplete ? "Puede marcar como completado" : `Vea al menos 98% del video para marcar como completado (${watchPercent}% actual)`}</p>
          </div>
          <Button size="sm" variant="secondary" disabled={!canMarkComplete || isCompleted}
            onClick={() => saveProgress(video.duracion_segundos || 600, video.duracion_segundos || 600, true)}>
            <CheckCircle className="w-4 h-4 mr-2" />{isCompleted ? "Completado" : "Marcar completado"}
          </Button>
        </div>
      )}
    </div>
  );
}

function MiProgresoTab() {
  const { getAuthHeaders } = useAuth();
  const [cursos, setCursos] = useState([]);
  useEffect(() => { (async () => { try { const res = await axios.get(`${API}/capacitaciones/mi-progreso`, getAuthHeaders()); setCursos(res.data); } catch (e) { console.error(e); } })(); }, [getAuthHeaders]);
  return (
    <div className="space-y-4" data-testid="mi-progreso-tab">
      {cursos.length === 0 ? <div className="text-center py-16 text-gray-500"><TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-40" /><p>No hay cursos asignados</p></div> : (
        cursos.map(curso => (
          <div key={curso.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
            <div className="flex items-start justify-between mb-3"><div><h3 className="font-semibold text-gray-900 dark:text-white">{curso.titulo}</h3><p className="text-sm text-gray-500">{curso.descripcion}</p></div>
              <span className={`text-sm font-bold ${curso.porcentaje_curso >= 100 ? "text-green-600" : "text-blue-600"}`}>{curso.porcentaje_curso}%</span></div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2"><div className={`h-2.5 rounded-full transition-all ${curso.porcentaje_curso >= 100 ? "bg-green-500" : "bg-blue-600"}`} style={{ width: `${curso.porcentaje_curso}%` }} /></div>
            <p className="text-xs text-gray-500">{curso.videos_completados}/{curso.total_videos} videos completados{curso.tiene_certificado && curso.porcentaje_curso >= 100 && <span className="ml-2 text-amber-600 font-medium"> - Certificado disponible</span>}</p>
          </div>
        ))
      )}
    </div>
  );
}

function DashboardTab() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState([]);
  useEffect(() => { (async () => { try { const res = await axios.get(`${API}/capacitaciones/dashboard`, getAuthHeaders()); setData(res.data); } catch (e) { console.error(e); } })(); }, [getAuthHeaders]);
  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      {data.length === 0 ? <div className="text-center py-16 text-gray-500"><BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-40" /><p>No hay datos de progreso aún</p></div> : (
        data.map(curso => (
          <div key={curso.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-gray-900 dark:text-white">{curso.titulo}</h3><span className="text-sm text-gray-500">{curso.total_videos} videos</span></div>
            {curso.users_progress?.length > 0 ? (
              <div className="space-y-3">{curso.users_progress.map((up, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="flex items-center gap-2 w-48"><Users className="w-4 h-4 text-gray-400" /><span className="text-sm truncate">{up.user_name || "Usuario"}</span></div>
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2"><div className="bg-blue-600 h-2 rounded-full" style={{ width: `${up.total > 0 ? (up.completed / up.total * 100) : 0}%` }} /></div>
                  <span className="text-sm text-gray-600 w-20 text-right">{up.completed}/{up.total}</span>
                </div>
              ))}</div>
            ) : <p className="text-sm text-gray-500">Ningún usuario ha iniciado este curso</p>}
          </div>
        ))
      )}
    </div>
  );
}

function CertificadosTab() {
  const { getAuthHeaders } = useAuth();
  const [certs, setCerts] = useState([]);
  useEffect(() => { (async () => { try { const res = await axios.get(`${API}/capacitaciones/certificados-emitidos`, getAuthHeaders()); setCerts(res.data); } catch (e) { console.error(e); } })(); }, [getAuthHeaders]);
  return (
    <div data-testid="certificados-tab">
      {certs.length === 0 ? <div className="text-center py-16 text-gray-500"><Award className="w-12 h-12 mx-auto mb-4 opacity-40" /><p>No se han emitido certificados aún</p></div> : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm"><thead className="bg-gray-50 dark:bg-gray-900"><tr><th className="text-left px-4 py-3 font-medium">Usuario</th><th className="text-left px-4 py-3 font-medium">Curso</th><th className="text-left px-4 py-3 font-medium">Fecha</th></tr></thead>
            <tbody>{certs.map(c => (<tr key={c.id} className="border-t border-gray-100 dark:border-gray-700"><td className="px-4 py-3">{c.user_name}</td><td className="px-4 py-3">{c.curso_titulo}</td><td className="px-4 py-3 text-gray-500">{new Date(c.fecha_emision).toLocaleDateString()}</td></tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
